/**
 * Relationship price comparison, browser side.
 *
 * `supabase/functions/_shared/priceComparison.mjs` is the canonical
 * implementation. It owns the deterministic pack parsing (Recipe Lab
 * conversion table — which deliberately has no browser copy, see
 * countUnitModel.js) and persists its results: the item's comparison_*
 * columns plus each option's conversion_snapshot.price_comparison.
 *
 * What is mirrored here is everything that needs NO conversion table:
 *
 *   - reading the stored comparison back (v2 snapshot, legacy ai_objective)
 *   - the automatic lowest-trustworthy-price selection rule
 *   - the display view-model every surface renders from
 *
 * All pure selection and division over stored values, so it runs on every
 * keystroke, offline, without a round trip. `priceComparison.parity.test.js`
 * runs shared fixtures through this file AND the edge module and pins the two
 * to identical answers — change one side, change both.
 *
 * Costs are compared UNROUNDED (recomputed as package_price ÷ stored
 * quantity, never read from a rounded column), and a missing quantity or
 * price is null — never 0.
 */

import { vendorIsRetired } from '../../../supabase/functions/_shared/vendorState.mjs';
import {
  convertBasisQuantity,
  hasPackageBasisConflict,
  sameComparisonUnit,
  validateComparisonAgainstBasis,
} from '../../../supabase/functions/_shared/packageBasis.mjs';

export const COMPARISON_AI_CONFIDENCE_THRESHOLD = 0.65;
export const COMPARISON_STALE_AFTER_DAYS = 90;

const COMPARISON_SOURCES = ['deterministic', 'ai_estimate', 'manual_override'];

const toPositiveNumber = (value) => {
  // Number, not parseFloat: the edge canonical rejects '10 lb' outright and
  // the mirror must reject it identically, or the same row compares
  // differently in the browser than on the server.
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/* ── Stored-comparison reader (mirror of readPriceComparison) ────────────── */

export function readPriceComparison(option) {
  const snap = option?.conversion_snapshot;
  if (!snap || typeof snap !== 'object') return null;

  const v2 = snap.price_comparison;
  if (v2 && typeof v2 === 'object' && toPositiveNumber(v2.quantity_per_purchase_package)) {
    return {
      version: Number(v2.version) || 2,
      relationshipUnit: v2.relationship_unit ?? null,
      unitFamily: v2.unit_family ?? null,
      quantityPerPurchasePackage: Number(v2.quantity_per_purchase_package),
      source: COMPARISON_SOURCES.includes(v2.source) ? v2.source : 'ai_estimate',
      confidence: Number.isFinite(Number(v2.confidence)) ? Number(v2.confidence) : null,
      assumption: v2.assumption ?? null,
      reasoning: v2.reasoning ?? null,
      verified: v2.verified === true,
      calculatedAt: v2.calculated_at ?? null,
      // v3: the fingerprint of the order package this comparison measured.
      // Absent on legacy snapshots; readers validate those against the
      // current package instead of trusting them blind.
      packageBasisFingerprint: v2.package_basis_fingerprint ?? null,
      // The AI's own account of HOW it got the quantity — 'explicit',
      // 'derived_from_pack', 'ai_inferred_standard_pack',
      // 'ai_inferred_contextual'. This, not who wrote the row, is what the
      // Est mark describes.
      dataQuality: v2.data_quality ?? null,
    };
  }

  const ai = snap.ai_objective;
  if (ai && typeof ai === 'object') {
    const qty = toPositiveNumber(ai.qty_per_objective_unit);
    const unit = ai.objective_unit ?? ai.unit ?? null;
    if (qty && unit) {
      return {
        version: 1,
        relationshipUnit: String(unit),
        unitFamily: null,
        quantityPerPurchasePackage: qty,
        source: 'ai_estimate',
        confidence: Number.isFinite(Number(ai.confidence)) ? Number(ai.confidence) : null,
        assumption: null,
        reasoning: ai.reasoning ?? null,
        verified: false,
        calculatedAt: null,
      };
    }
    const directCost = toPositiveNumber(ai.cost_per_unit ?? ai.price);
    if (directCost && (ai.unit || ai.objective_unit)) {
      return {
        version: 1,
        relationshipUnit: String(ai.unit ?? ai.objective_unit),
        unitFamily: null,
        quantityPerPurchasePackage: null,
        source: 'ai_estimate',
        confidence: Number.isFinite(Number(ai.confidence)) ? Number(ai.confidence) : null,
        assumption: null,
        reasoning: ai.reasoning ?? null,
        verified: false,
        calculatedAt: null,
        comparableUnitCostOverride: directCost,
      };
    }
  }
  return null;
}

/* ── Stored-comparison assembly (mirror of assembleStoredComparison) ─────── */

const activeOf = (options) =>
  (Array.isArray(options) ? options : []).filter(
    (o) => o && o.status !== 'inactive' && o.id != null,
  );

const optionEntry = (option, { quantity, source, confidence, assumption = null, estimated = false }) => {
  const price = toPositiveNumber(option.package_price);
  return {
    optionId: option.id,
    quantityPerPurchasePackage: quantity,
    comparableUnitCost: price != null && quantity != null ? price / quantity : null,
    source,
    confidence,
    assumption,
    estimated: estimated === true,
  };
};

const INFERRED_DATA_QUALITIES = new Set(['ai_inferred_standard_pack', 'ai_inferred_contextual']);

/**
 * Was the comparison quantity INFERRED, as opposed to read or calculated from
 * the package? Drives the quiet "Est" disclosure and nothing else — an
 * inferred quantity is exactly as eligible to win on price as an explicit one.
 */
function isInferredQuantity(stored, verdict) {
  if (!stored) return false;
  if (stored.dataQuality) return INFERRED_DATA_QUALITIES.has(stored.dataQuality);
  return stored.source === 'ai_estimate' && verdict?.proven !== true;
}

/**
 * Express a stored snapshot's quantity in the group's comparison unit.
 * Returns { quantity, reason } — quantity null with a machine-readable reason
 * when the snapshot cannot reach that unit at all.
 */
function quantityInComparisonUnit(stored, unit) {
  if (!stored) return { quantity: null, reason: 'no_stored_comparison' };
  const storedQty = toPositiveNumber(stored.quantityPerPurchasePackage);
  if (storedQty == null) return { quantity: null, reason: 'no_stored_quantity' };
  if (sameComparisonUnit(stored.relationshipUnit, unit)) return { quantity: storedQty, reason: null };
  const converted = toPositiveNumber(convertBasisQuantity(storedQty, stored.relationshipUnit, unit));
  if (converted != null) return { quantity: converted, reason: null };
  return { quantity: null, reason: 'unit_not_convertible' };
}

export function assembleStoredComparison(item = {}, options = [], context = {}) {
  const active = activeOf(options);
  // A pooled EQUIVALENT group hands in options belonging to SEVERAL items.
  // A package basis (and the fingerprint stamped on its snapshot) is a fact
  // about the option's OWN item — resolving a sibling's option against the
  // canonical item recomputes a different fingerprint and silently discards a
  // perfectly good comparison. `itemsById` lets the caller supply the owning
  // item per option; without it every option keeps using `item`, unchanged.
  const itemsById = context?.itemsById || {};
  const itemFor = (option) => itemsById[String(option?.inventory_item_id)] || item;
  // `comparisonUnit` lets the group layer impose ONE denominator on every
  // member (equivalent_group_comparisons.comparison_unit) instead of each
  // member's own column disagreeing with its siblings'.
  let unit = context?.comparisonUnit ?? item?.comparison_unit ?? null;
  let inferred = false;
  if (!unit) {
    // Callers that only hold option rows (allocation needs, count lines) can
    // still compare: every v2 snapshot in one relationship was written with
    // the SAME relationship_unit, so a unanimous unit is authoritative.
    // Version-1 (legacy ai_objective) units were chosen per option and never
    // qualify.
    // Unanimity is about the physical unit, not its spelling: "gal" and
    // "gallon" are one answer written twice.
    const units = [];
    active.forEach((option) => {
      const stored = readPriceComparison(option);
      if (stored?.version >= 2 && stored.relationshipUnit
        && !units.some((seen) => sameComparisonUnit(seen, stored.relationshipUnit))) {
        units.push(stored.relationshipUnit);
      }
    });
    if (units.length === 1) {
      unit = units[0];
      inferred = true;
    }
  }
  if (!unit) {
    return {
      comparisonUnit: null,
      comparisonUnitLabel: null,
      unitFamily: null,
      source: 'unresolved',
      confidence: null,
      reasoning: null,
      options: [],
      unresolvedOptionIds: active.map((o) => o.id),
    };
  }
  const entries = [];
  const unresolved = [];
  const unresolvedDetails = [];
  active.forEach((option) => {
    const stored = readPriceComparison(option);
    const ownItem = itemFor(option);
    // Reach the group's denominator from whatever the snapshot was written
    // in: the same physical unit spelled differently needs no arithmetic, and
    // a sibling unit in the same family converts exactly. Only a genuinely
    // different quantity (a weight against a volume) fails to arrive.
    const { quantity, reason: reachReason } = quantityInComparisonUnit(stored, unit);
    // A stored comparison speaks only for the package it measured. An option
    // whose package facts conflict has no agreed package; a snapshot whose
    // fingerprint no longer matches, or whose quantity provably disagrees
    // with the current package, is stale. Those land in unresolved — they
    // must not rank, select, or badge. A snapshot that can be neither proven
    // nor disproven (an AI bridge, an unresolved order-side conversion)
    // stays, flagged basisVerified:false so titles require proof.
    const conflicted = quantity != null && hasPackageBasisConflict(option, { item: ownItem });
    const verdict = quantity != null && !conflicted
      ? validateComparisonAgainstBasis(option, stored, { item: ownItem })
      : null;
    if (verdict && verdict.usable) {
      entries.push({
        ...optionEntry(option, {
          quantity,
          source: stored.source,
          confidence: stored.confidence,
          assumption: stored.assumption,
          // "Estimated" describes the QUANTITY, not who touched the record.
          // "1000 ct box" is 1000 each whoever wrote it down; a 140-count case
          // of lemons weighing ~40 lb is a genuine inference. The AI states
          // which it did in data_quality, so that is what the mark reads.
          // Legacy snapshots carry no data_quality: fall back to the source
          // label, softened by proof — a quantity the deterministic parser can
          // confirm from the pack text was not estimated by anyone.
          estimated: isInferredQuantity(stored, verdict),
        }),
        basisVerified: verdict.proven,
      });
    } else {
      unresolved.push(option.id);
      // Never a bare blank: every option that cannot be compared carries the
      // reason it could not, so the surface can say what needs confirming.
      unresolvedDetails.push({
        optionId: option.id,
        reason: conflicted ? 'package_basis_conflict' : (verdict?.reason ?? reachReason),
      });
    }
  });
  // An inferred unit takes its provenance from the snapshots themselves: the
  // weakest entry speaks for the relationship.
  let source = COMPARISON_SOURCES.includes(item.comparison_unit_source) ? item.comparison_unit_source : 'unresolved';
  let confidence = Number.isFinite(Number(item.comparison_unit_confidence)) ? Number(item.comparison_unit_confidence) : null;
  if (inferred && entries.length > 0) {
    source = entries.some((e) => e.source === 'ai_estimate') ? 'ai_estimate' : 'deterministic';
    const confidences = entries.map((e) => Number(e.confidence)).filter((c) => Number.isFinite(c));
    confidence = confidences.length ? Math.min(...confidences) : null;
  }
  return {
    comparisonUnit: unit,
    comparisonUnitLabel: unit,
    unitFamily: item.comparison_unit_family ?? null,
    source,
    confidence,
    reasoning: item.comparison_unit_reasoning ?? null,
    options: entries,
    unresolvedOptionIds: unresolved,
    unresolvedDetails,
  };
}

/* ── Automatic selection (mirror of selectAutomaticOption) ───────────────── */

export function isComparisonPriceStale(option, now = Date.now()) {
  const t = Date.parse(option?.effective_date);
  if (!Number.isFinite(t)) return true;
  return now - t > COMPARISON_STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

const eligibleForPurchase = (option) =>
  !!option &&
  option.status === 'active' &&
  option.is_approved === true &&
  !option.is_hidden_from_allocation &&
  // A retired vendor never wins the automatic selection — its package may
  // still describe stock on the shelf, but it is not a way to BUY.
  !vendorIsRetired(option) &&
  toPositiveNumber(option.package_price) != null &&
  // A package whose stored facts conflict has no single orderable package to
  // win with. Entries for such options are already dropped at assembly; this
  // guards callers that hand in a comparison assembled elsewhere.
  !hasPackageBasisConflict(option);

export function selectAutomaticOption({ options = [], comparison = null, now = Date.now() } = {}) {
  const none = { optionId: null, reason: 'unresolved', tiedOptionIds: [], usedStale: false, confidence: null };
  if (!comparison || !comparison.comparisonUnit || !Array.isArray(comparison.options)) return none;

  const entryById = new Map(comparison.options.map((e) => [String(e.optionId), e]));
  const candidates = [];
  activeOf(options).forEach((option) => {
    if (!eligibleForPurchase(option)) return;
    const entry = entryById.get(String(option.id));
    if (!entry || toPositiveNumber(entry.quantityPerPurchasePackage) == null) return;
    if (entry.comparableUnitCost == null) return;
    if (entry.source === 'ai_estimate') {
      const conf = Number(entry.confidence);
      if (!Number.isFinite(conf) || conf < COMPARISON_AI_CONFIDENCE_THRESHOLD) return;
    }
    candidates.push({ option, entry, stale: isComparisonPriceStale(option, now) });
  });
  if (candidates.length === 0) return none;

  const fresh = candidates.filter((c) => !c.stale);
  const pool = fresh.length > 0 ? fresh : candidates;
  const usedStale = fresh.length === 0;

  let bestCost = Infinity;
  pool.forEach((c) => { if (c.entry.comparableUnitCost < bestCost) bestCost = c.entry.comparableUnitCost; });
  const tied = pool.filter((c) => c.entry.comparableUnitCost === bestCost);

  let winner = tied[0];
  let reason = 'best_price';
  if (tied.length > 1) {
    reason = 'tied_best_price';
    const preferred = tied.find((c) => c.option.is_preferred === true);
    if (preferred) {
      winner = preferred;
    } else {
      const verified = tied
        .filter((c) => c.option.verification_status === 'verified' && Number.isFinite(Date.parse(c.option.effective_date)))
        .sort((a, b) => Date.parse(b.option.effective_date) - Date.parse(a.option.effective_date));
      winner = verified[0]
        ?? [...tied].sort((a, b) => String(a.option.id).localeCompare(String(b.option.id)))[0];
    }
  } else if (winner.entry.source === 'ai_estimate') {
    reason = 'best_price_ai_estimate';
  }

  return {
    optionId: String(winner.option.id),
    reason,
    tiedOptionIds: tied.length > 1 ? tied.map((c) => String(c.option.id)) : [],
    usedStale,
    confidence: winner.entry.confidence ?? null,
  };
}

/* ── Formatting ──────────────────────────────────────────────────────────── */

/**
 * Half-up decimal rounding via exponent shift — `(5.425).toFixed(2)` is
 * "5.42" because the float sits just under 5.425, but a person doing the
 * arithmetic writes $5.43. Display must match the person.
 */
export function roundHalfUpFixed(value, decimals) {
  const shifted = Math.round(Number(`${value}e${decimals}`));
  return (shifted / 10 ** decimals).toFixed(decimals);
}

export function formatComparablePrice(cost, unitLabel, { decimals = 2 } = {}) {
  const parsed = Number.parseFloat(cost);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const amount = `$${roundHalfUpFixed(parsed, decimals)}`;
  return unitLabel ? `${amount}/${unitLabel}` : amount;
}

/**
 * 'per case', 'per bag', 'per jug' — the option's real purchase unit, never a
 * generic 'per package' when a better word exists.
 */
export function purchaseUnitLabel(option) {
  const raw = String(option?.purchase_unit ?? '').trim().toLowerCase();
  if (!raw) return 'per package';
  return `per ${raw}`;
}

/**
 * Pick the display precision that keeps the winner visibly cheaper. If the
 * lowest and a non-lowest cost render identically at 2dp, precision rises to
 * 3 then 4; if they are STILL identical at 4dp the difference is sub-cent
 * noise — keep 2dp and say so in a note instead of a misleading tag.
 */
/**
 * Raise precision until no real cost prints as "$0.00". A 5000-count case of
 * straws at $22.95 is $0.00459 each; rounding that to two places prints free,
 * which is worse than imprecise. Capped at 4, comparablePrecision's ceiling.
 * This used to live only in the Related Products card, so every other surface
 * rendering from this view-model still printed $0.00 for sub-cent items.
 */
function nonZeroDecimals(costs, from) {
  let decimals = from;
  while (
    decimals < 4
    && costs.some((cost) => Number.parseFloat(roundHalfUpFixed(cost, decimals)) === 0)
  ) {
    decimals += 1;
  }
  return decimals;
}

export function comparablePrecision(costs) {
  const positive = (costs || []).filter((c) => Number.isFinite(c) && c > 0);
  if (positive.length < 2) return { decimals: 2, subCent: false };
  const lowest = Math.min(...positive);
  const others = positive.filter((c) => c !== lowest);
  if (others.length === 0) return { decimals: 2, subCent: false }; // genuine tie
  for (const decimals of [2, 3, 4]) {
    const lowestText = roundHalfUpFixed(lowest, decimals);
    if (others.every((c) => roundHalfUpFixed(c, decimals) !== lowestText)) {
      return { decimals: nonZeroDecimals(positive, decimals), subCent: false };
    }
  }
  return { decimals: nonZeroDecimals(positive, 2), subCent: true };
}

const STATUS_LABELS = {
  manual_verified: 'Verified',
  deterministic: 'Calculated',
  ai_estimate: 'AI estimate',
  unresolved: 'Needs review',
};

export function comparisonStatusLabel(entry) {
  if (!entry) return STATUS_LABELS.unresolved;
  if (entry.source === 'manual_override') return STATUS_LABELS.manual_verified;
  return STATUS_LABELS[entry.source] ?? STATUS_LABELS.unresolved;
}

/* ── The shared display view-model ───────────────────────────────────────── */

/**
 * Everything a surface needs to present one item's vendor options coherently.
 * All surfaces (vendor picker, related products, options editor, shopping
 * list, reports) render from this — none of them does unit math of its own.
 *
 * @param {object} args { item, options, selectedOptionId, vendorNameById, now }
 * @returns {{
 *   comparisonUnit, comparisonAvailable, comparisonSource, comparisonConfidence,
 *   needsReview, subCentNote,
 *   selection: { optionId, reason, isExplicit, usedStale },
 *   options: [{ optionId, vendorName, productName, packDescription,
 *               packagePrice, packagePriceDisplay, purchaseUnitLabel,
 *               comparableUnitCost, comparableDisplay, quantityPerPurchasePackage,
 *               source, statusLabel, confidence, estimated, assumption,
 *               isLowest, isTiedLowest, isSelected, selectionLabel,
 *               isStale, needsReview }]
 * }}
 */
export function buildComparablePriceDisplay({
  item = {},
  options = [],
  selectedOptionId = null,
  vendorNameById = {},
  now = Date.now(),
  // Folded EQUIVALENT groups: the item each option actually belongs to, and
  // the one denominator the group agreed on. Both default to the old
  // single-item behaviour.
  itemsById = {},
  comparisonUnit = null,
} = {}) {
  const comparison = assembleStoredComparison(item, options, { itemsById, comparisonUnit });
  const unresolvedReasonById = new Map(
    (comparison.unresolvedDetails || []).map((d) => [String(d.optionId), d.reason]),
  );
  const active = activeOf(options);
  const entryById = new Map(comparison.options.map((e) => [String(e.optionId), e]));

  const explicit = selectedOptionId != null && selectedOptionId !== ''
    ? active.find((o) => String(o.id) === String(selectedOptionId)) ?? null
    : null;
  const auto = selectAutomaticOption({ options, comparison, now });
  const selection = explicit
    ? { optionId: String(explicit.id), reason: 'explicit', isExplicit: true, usedStale: false }
    : { optionId: auto.optionId, reason: auto.reason, isExplicit: false, usedStale: auto.usedStale };

  const costs = comparison.options
    .map((e) => e.comparableUnitCost)
    .filter((c) => Number.isFinite(c) && c > 0);
  const { decimals, subCent } = comparablePrecision(costs);
  const lowestCost = costs.length ? Math.min(...costs) : null;
  const lowestIds = new Set(
    comparison.options.filter((e) => e.comparableUnitCost === lowestCost).map((e) => String(e.optionId)),
  );
  const genuineTie = lowestIds.size > 1;

  const rows = active.map((option) => {
    const entry = entryById.get(String(option.id)) ?? null;
    const isSelected = selection.optionId != null && String(option.id) === String(selection.optionId);
    const isLowestHere = entry != null && lowestCost != null && entry.comparableUnitCost === lowestCost;
    let selectionLabel = null;
    if (isSelected) {
      if (selection.reason === 'explicit') selectionLabel = 'SELECTED';
      else if (selection.reason === 'preferred_fallback') selectionLabel = 'PREFERRED FALLBACK';
      else selectionLabel = 'AUTO — LOWEST PRICE';
    }
    const vendorName =
      (option.vendor_id != null && vendorNameById[option.vendor_id])
      || String(option.vendor_name_text ?? '').trim()
      || String(item.vendor ?? '').trim()
      || 'Vendor not set';
    return {
      optionId: String(option.id),
      vendorName,
      productName: String(option.vendor_description ?? '').trim() || String(item.description ?? '').trim() || 'Vendor product',
      packDescription: String(option.pack_description ?? '').trim() || String(item.pack_size ?? '').trim() || null,
      packagePrice: toPositiveNumber(option.package_price),
      packagePriceDisplay: formatComparablePrice(option.package_price, null),
      purchaseUnitLabel: purchaseUnitLabel(option),
      quantityPerPurchasePackage: entry?.quantityPerPurchasePackage ?? null,
      comparableUnitCost: entry?.comparableUnitCost ?? null,
      comparableDisplay: entry?.comparableUnitCost != null
        ? formatComparablePrice(entry.comparableUnitCost, comparison.comparisonUnitLabel, { decimals })
        : null,
      source: entry?.source ?? null,
      statusLabel: comparisonStatusLabel(entry),
      confidence: entry?.confidence ?? null,
      estimated: entry?.estimated === true,
      assumption: entry?.assumption ?? null,
      isLowest: isLowestHere && !genuineTie,
      isTiedLowest: isLowestHere && genuineTie,
      isSelected,
      selectionLabel,
      isStale: isComparisonPriceStale(option, now),
      needsReview: entry == null,
      // Why this option has no comparable price. Null when it has one.
      unresolvedReason: entry == null ? (unresolvedReasonById.get(String(option.id)) ?? 'no_stored_comparison') : null,
    };
  });

  return {
    comparisonUnit: comparison.comparisonUnit,
    comparisonUnitLabel: comparison.comparisonUnitLabel,
    comparisonAvailable: comparison.comparisonUnit != null && costs.length > 0,
    comparisonSource: comparison.source,
    comparisonConfidence: comparison.confidence,
    needsReview: comparison.comparisonUnit == null || comparison.unresolvedOptionIds.length > 0,
    subCentNote: subCent && comparison.comparisonUnitLabel
      ? `less than 1¢/${comparison.comparisonUnitLabel} difference`
      : null,
    selection,
    options: rows,
    unresolvedDetails: comparison.unresolvedDetails || [],
  };
}

/* ── Frozen-snapshot reader (reports) ────────────────────────────────────── */

/**
 * Present a comparison from a FROZEN snapshot (allocation option_snapshot,
 * shopping_list_items.comparison_snapshot) — historical reports must show the
 * facts as decided, never today's recalculation. Accepts either the v2
 * price_comparison shape or the flat comparison_snapshot shape.
 */
export function comparableFromSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const unit = snapshot.comparison_unit ?? snapshot.relationship_unit ?? null;
  const qty = toPositiveNumber(snapshot.quantity_per_purchase_package);
  const cost = toPositiveNumber(snapshot.comparable_unit_cost);
  if (!unit || (!qty && !cost)) return null;
  return {
    comparisonUnit: unit,
    quantityPerPurchasePackage: qty,
    comparableUnitCost: cost,
    comparableDisplay: cost != null ? formatComparablePrice(cost, unit) : null,
    source: snapshot.source ?? null,
    confidence: Number.isFinite(Number(snapshot.confidence)) ? Number(snapshot.confidence) : null,
    estimated: snapshot.estimated === true || snapshot.source === 'ai_estimate',
    effectiveDate: snapshot.effective_date ?? null,
  };
}
